// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import Linked from 'shared-components/service/Linked'
import styles from './DeclaredStrip.module.css'
import DeclaredFingerprint from './DeclaredFingerprint'
import { CLEAR_DECLARED } from 'lib/owner-notes/band-copy'
import type { DeclaredProduct } from 'lib/owner-notes/declared'

/** The ask on the strip: the form's own buy-again question, its two answers open the band. */
export interface DeclaredStripAsk {
  question: string
  body: string
  answers: { yes: string; no: string }
  onAnswer: (buyAgain: boolean) => void
  /** The surface hands off to the community writer (`useWriterHandoff`): each answer is a link
   *  to it, same classes, carrying the answer; `onAnswer` is not called. */
  hrefFor?: (buyAgain: boolean) => string
  /** With `hrefFor`: `target` / `rel` for a desktop's new tab (`useWriterHandoff().linkProps`). */
  linkProps?: { target?: '_blank'; rel?: string }
}

interface DeclaredStripProps {
  declared: DeclaredProduct
  /** "Compared with your <name>". */
  lead: string
  /** What the declaration changed on THIS page (a guide's charts, a category's cards). */
  body: string
  /** The reader's own note status, beside the lead; null without a note. */
  statusLabel: string | null
  /** `SWITCH_LABEL` once the reader has a note, else `UNDECLARE_LABEL`: clears and opens the
   *  picker for another. */
  clearLabel: string
  onClear: () => void
  /** "Clear": drops the declared pair and picks nothing (2026-10-30). */
  onRemove: () => void
  /** Null = nothing asked: no Turnstile key, `/me` pending, an author, or the band is open. */
  ask: DeclaredStripAsk | null
  /** In the ask's place when the strip asks nothing itself (a guide, 2026-10-17: the one ask
   *  sits on the pick the reader owns, and the strip points at it). */
  aside?: React.ReactNode
}

/** The mark when there is no product photo to show: headphones, line-drawn. */
export const IconHeadphones: React.VFC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M4 14v-2a8 8 0 0 1 16 0v2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <rect x="3" y="13.5" width="4.5" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <rect
      x="16.5"
      y="13.5"
      width="4.5"
      height="7"
      rx="1.6"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
)

/**
 * The reference strip: the declared pair, its fingerprint (the page's one "not lab-tested"
 * disclosure, Directive 21f605aa), the switch, and the ask as a question. One declared state on
 * a best-list article and on a category page (2026-09-18) — presentational: the caller owns the
 * declaration, the own note, the band and every event.
 *
 * Extracted from `GuideCapture` with its markup and CSS unchanged.
 */
const DeclaredStrip: React.VFC<DeclaredStripProps> = ({
  declared,
  lead,
  body,
  statusLabel,
  clearLabel,
  onClear,
  onRemove,
  ask,
  aside,
}) => (
  <div className={styles.strip}>
    <span className={styles.mark}>
      {declared.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className={styles.thumb}
          src={declared.imageUrl}
          alt=""
          width={40}
          height={40}
          loading="lazy"
        />
      ) : (
        <IconHeadphones />
      )}
    </span>
    <div className={styles.stripMain}>
      <p className={styles.stripLead}>
        {lead}
        {statusLabel && <span className={styles.statusChip}>{statusLabel}</span>}
      </p>
      <p className={styles.stripBody}>{body}</p>
      <div className={styles.stripMeta}>
        <DeclaredFingerprint declared={declared} pills />
        <button type="button" className={styles.switch} onClick={onClear}>
          {clearLabel}
        </button>
        <button
          type="button"
          className={styles.remove}
          aria-label={CLEAR_DECLARED.aria(declared.name)}
          onClick={onRemove}
        >
          <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
            <path
              d="M3 3l6 6M9 3l-6 6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          {CLEAR_DECLARED.label}
        </button>
      </div>
    </div>
    {!ask && aside}
    {ask && (
      // The ask is the form's own buy-again question; the answer opens the band with it.
      <div className={styles.ask} role="group" aria-label={ask.question}>
        <div className={styles.askMain}>
          <p className={styles.askTitle}>{ask.question}</p>
          <p className={styles.askBody}>{ask.body}</p>
        </div>
        {ask.hrefFor ? (
          <div className={styles.askAnswers}>
            <Linked
              href={ask.hrefFor(true)}
              className={`${styles.primary} ${styles.link}`}
              {...ask.linkProps}
            >
              {ask.answers.yes}
            </Linked>
            <Linked
              href={ask.hrefFor(false)}
              className={`${styles.answer} ${styles.link}`}
              {...ask.linkProps}
            >
              {ask.answers.no}
            </Linked>
          </div>
        ) : (
          <div className={styles.askAnswers}>
            <button type="button" className={styles.primary} onClick={() => ask.onAnswer(true)}>
              {ask.answers.yes}
            </button>
            <button type="button" className={styles.answer} onClick={() => ask.onAnswer(false)}>
              {ask.answers.no}
            </button>
          </div>
        )}
      </div>
    )}
  </div>
)

export default DeclaredStrip
